import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Loader2, CheckCircle2, Briefcase, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { supervisionTriggerStates } from "@/lib/supervisionStates";
import { PhoenixWordmark } from "@/components/PhoenixLogo";
import {
    inputCls, labelCls, EMPTY_LICENSE, EMPTY_DEGREE,
    CredentialSections, validateCredentials, buildCredentialPayload,
} from "@/components/credentialing/ProviderCredentialFields";

function Section({ title, children, when = true }) {
    if (!when) return null;
    return (
        <div className="bg-white border border-border/70 rounded-xl p-6">
            <h2 className="font-display font-semibold text-lg text-phoenix-navy mb-4">{title}</h2>
            {children}
        </div>
    );
}

export default function ApplyPage() {
    const [form, setForm] = useState({
        provider_type: "therapist", provider_role: "behavioral_health",
        specialty: "", credentials: "",
        first_name: "", last_name: "", email: "", phone: "", date_of_birth: "",
        address: "", city: "", state: "", zip_code: "",
        npi_number: "", dea_number: "", dea_state: "", dea_expiration: "",
        controlled_substance_number: "", controlled_substance_state: "", controlled_substance_expiration: "",
        drivers_license_expiration: "",
        supervising_physician_first_name: "", supervising_physician_last_name: "",
        supervising_physician_title: "MD", supervising_physician_phone: "",
        supervising_physician_email: "", supervising_physician_npi: "", supervising_physician_dea: "",
        collaborative_agreement_expiration: "",
        board_certification_board: "", board_certification_type: "",
        board_certification_number: "", board_certification_expiration: "",
        malpractice_insurance_carrier: "", malpractice_policy_number: "", malpractice_expiration: "",
        years_of_experience: "", certifications: "", skills_narrative: "",
        forensic_cst_qualified: false,
        has_pending_criminal_charges: false, criminal_charges_disclosure: "",
        has_felony_history: false, felony_history_disclosure: "",
        has_license_suspension: false, license_restriction_disclosure: "",
        has_malpractice_claims: false, malpractice_claims_disclosure: "",
        w9_legal_name: "", w9_business_name: "", w9_tin_type: "ssn", w9_tax_id: "",
        w9_address: "", w9_city_state_zip: "", w9_certification: false,
    });
    const [licenses, setLicenses] = useState([{ ...EMPTY_LICENSE }]);
    const [degrees, setDegrees] = useState([{ ...EMPTY_DEGREE }]);
    const [docs, setDocs] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [done, setDone] = useState(false);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const canonicalType = form.provider_type === "therapist"
        ? "therapist" : (form.provider_role === "primary_care" ? "medical" : "psych");
    const [appTpl, setAppTpl] = useState(null);
    useEffect(() => {
        let active = true;
        api.get(`/application-template/${canonicalType}`)
            .then((r) => { if (active) setAppTpl(r.data); })
            .catch(() => { if (active) setAppTpl(null); });
        return () => { active = false; };
    }, [canonicalType]);
    const fieldMode = (key) => appTpl?.fields?.[key] || "optional";
    const showField = (key) => fieldMode(key) !== "hidden";
    const setLic = (i, k, v) => setLicenses((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
    const setDeg = (i, k, v) => setDegrees((ds) => ds.map((d, idx) => (idx === i ? { ...d, [k]: v } : d)));
    const setDoc = (key) => (file) => setDocs((d) => ({ ...d, [key]: file }));
    const isProvider = form.provider_type === "provider";
    const supTriggerStates = supervisionTriggerStates(
        form.credentials,
        [form.state, form.dea_state, ...licenses.map((l) => l.state)],
    );
    const supMode = appTpl?.supervising_physician || "state_based";
    const showSupervising = isProvider && supMode !== "hidden";
    const supRequired = showSupervising && (supMode === "required" || supTriggerStates.length > 0);

    const submit = async () => {
        setError("");
        const verr = validateCredentials({ form, licenses, docs, isProvider, supRequired, supTriggerStates, showField });
        if (verr) { setError(verr); window.scrollTo(0, 0); return; }
        setSubmitting(true);
        try {
            const payload = {
                provider_type: form.provider_type, provider_role: form.provider_role,
                specialty: form.specialty, credentials: form.credentials,
                first_name: form.first_name, last_name: form.last_name, email: form.email,
                phone: form.phone, date_of_birth: form.date_of_birth || null,
                address: form.address, city: form.city, state: form.state, zip_code: form.zip_code,
                npi_number: form.npi_number,
                w9_legal_name: form.w9_legal_name, w9_business_name: form.w9_business_name || null,
                w9_tin_type: form.w9_tin_type, w9_tax_id: form.w9_tax_id,
                w9_address: form.w9_address, w9_city_state_zip: form.w9_city_state_zip,
                w9_certification: form.w9_certification,
                specialty: form.specialty || null,
                ...buildCredentialPayload({ form, licenses, degrees, docs, isProvider }),
            };
            await api.post("/apply", payload);
            setDone(true);
            window.scrollTo(0, 0);
        } catch (e) {
            const d = e?.response?.data?.detail;
            setError(typeof d === "string" ? d : "Please complete all required fields (marked *).");
            window.scrollTo(0, 0);
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div className="min-h-screen bg-phoenix-mesh flex items-center justify-center px-6">
                <div data-testid="apply-success" className="max-w-md bg-white rounded-3xl shadow-phoenix-card border border-border/60 p-8 text-center">
                    <CheckCircle2 className="w-12 h-12 text-success mx-auto mb-4" />
                    <h1 className="font-display font-bold text-2xl text-phoenix-navy mb-2">Application received</h1>
                    <p className="text-sm text-muted-foreground">Our clinical team will review your credentials and reach out by email.</p>
                    <Link to="/" className="inline-block mt-6 text-sm font-semibold text-phoenix-navy hover:text-phoenix-orange">← Back to home</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-phoenix-bg">
            <header className="bg-white border-b border-border/70 px-6 py-4">
                <div className="max-w-3xl mx-auto flex items-center justify-between">
                    <Link to="/"><PhoenixWordmark /></Link>
                </div>
            </header>
            <div data-testid="apply-page" className="max-w-3xl mx-auto px-6 py-8 space-y-6">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-phoenix-navy rounded-lg flex items-center justify-center">
                        <Briefcase className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h1 className="font-display font-bold text-2xl text-phoenix-navy">
                            {isProvider ? "Provider Application Form" : "Case Manager Application Form"}
                        </h1>
                        <p className="text-sm text-muted-foreground">Telehealth-first behavioral health & medical care for correctional facilities</p>
                    </div>
                </div>

                {error && <div data-testid="apply-error" className="text-sm text-destructive bg-destructive/5 border border-destructive/15 rounded-lg px-4 py-2.5">{error}</div>}

                <Section title="Position">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <label className={labelCls}>Applying as *</label>
                            <select data-testid="apply-type-select" className={inputCls} value={form.provider_type}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    set("provider_type", v);
                                    set("provider_role", v === "therapist" ? "behavioral_health" : form.provider_role);
                                }}>
                                <option value="therapist">Case Manager (LCSW, LPC…)</option>
                                <option value="provider">Provider (MD, DO, NP, PA…)</option>
                            </select>
                        </div>
                        {isProvider && (
                            <div>
                                <label className={labelCls}>Specialty *</label>
                                <select data-testid="apply-role-select" className={inputCls} value={form.provider_role}
                                    onChange={(e) => {
                                        set("provider_role", e.target.value);
                                        set("specialty", e.target.value === "behavioral_health" ? "Psychiatry" : "Medical");
                                    }}>
                                    <option value="behavioral_health">Psychiatry</option>
                                    <option value="primary_care">Medical (Primary Care)</option>
                                </select>
                            </div>
                        )}
                        <div>
                            <label className={labelCls}>Credentials *</label>
                            <input data-testid="apply-credentials" className={inputCls} placeholder={isProvider ? "MD / DO / PMHNP-BC" : "LCSW / LPC / LMFT"}
                                value={form.credentials} onChange={(e) => set("credentials", e.target.value)} />
                        </div>
                        {!isProvider && (
                            <div>
                                <label className={labelCls}>Specialty</label>
                                <input className={inputCls} value={form.specialty} onChange={(e) => set("specialty", e.target.value)} />
                            </div>
                        )}
                    </div>
                </Section>

                <Section title="Personal Information">
                    {isProvider && (
                        <div data-testid="practice-address-warning" className="mb-4 flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
                            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                            <div>
                                <span className="font-semibold">Practice Address — Important.</span>{" "}
                                Enter the address on file with your state licensing board(s) and DEA registration. For telehealth
                                providers, this is your registered practice address — not a corporate or employer address. This
                                address will appear on all prescriptions and must match your DEA certificate.
                            </div>
                        </div>
                    )}
                    <div className="grid sm:grid-cols-2 gap-4">
                        {[["first_name", "First name *"], ["last_name", "Last name *"], ["email", "Email *"],
                          ["phone", "Phone number"], ["date_of_birth", "Date of birth *"], ["address", "Address *"],
                          ["city", "City *"], ["state", "State *"], ["zip_code", "ZIP code *"]].map(([k, label]) => (
                            <div key={k}>
                                <label className={labelCls}>{label}</label>
                                <input data-testid={`apply-${k}`} className={inputCls}
                                    type={k === "date_of_birth" ? "date" : k === "email" ? "email" : "text"}
                                    value={form[k]} onChange={(e) => set(k, e.target.value)} />
                                {k === "phone" && <p className="text-xs text-muted-foreground mt-1">This is the number the pharmacy will use to reach you for order clarifications — it prints on your prescriptions.</p>}
                            </div>
                        ))}
                        <div>
                            <label className={labelCls}>NPI number *</label>
                            <input data-testid="apply-npi" className={inputCls} placeholder="10-digit NPI" maxLength={10}
                                value={form.npi_number} onChange={(e) => set("npi_number", e.target.value)} />
                        </div>
                    </div>
                </Section>

                <CredentialSections
                    form={form} set={set}
                    licenses={licenses} setLic={setLic} setLicenses={setLicenses}
                    degrees={degrees} setDeg={setDeg} setDegrees={setDegrees}
                    docs={docs} setDoc={setDoc}
                    isProvider={isProvider} appTpl={appTpl} fieldMode={fieldMode} showField={showField}
                    supRequired={supRequired} supTriggerStates={supTriggerStates} showSupervising={showSupervising} />

                <Section title="W-9 (taxpayer information)">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <label className={labelCls}>Legal name *</label>
                            <input data-testid="apply-w9-name" className={inputCls} value={form.w9_legal_name} onChange={(e) => set("w9_legal_name", e.target.value)} />
                        </div>
                        <div>
                            <label className={labelCls}>Business name (if any)</label>
                            <input className={inputCls} value={form.w9_business_name} onChange={(e) => set("w9_business_name", e.target.value)} />
                        </div>
                        <div>
                            <label className={labelCls}>TIN type *</label>
                            <select className={inputCls} value={form.w9_tin_type} onChange={(e) => set("w9_tin_type", e.target.value)}>
                                <option value="ssn">SSN</option>
                                <option value="ein">EIN</option>
                            </select>
                        </div>
                        <div>
                            <label className={labelCls}>Tax ID *</label>
                            <input data-testid="apply-w9-tin" className={inputCls} value={form.w9_tax_id} onChange={(e) => set("w9_tax_id", e.target.value)} />
                        </div>
                        <div>
                            <label className={labelCls}>Address *</label>
                            <input data-testid="apply-w9-address" className={inputCls} value={form.w9_address} onChange={(e) => set("w9_address", e.target.value)} />
                        </div>
                        <div>
                            <label className={labelCls}>City, State ZIP *</label>
                            <input data-testid="apply-w9-csz" className={inputCls} value={form.w9_city_state_zip} onChange={(e) => set("w9_city_state_zip", e.target.value)} />
                        </div>
                    </div>
                    <label className="flex items-start gap-2 text-sm text-foreground mt-4">
                        <input data-testid="apply-w9-cert" type="checkbox" className="w-4 h-4 mt-0.5 accent-phoenix-navy"
                            checked={form.w9_certification} onChange={(e) => set("w9_certification", e.target.checked)} />
                        I certify, under penalties of perjury, that the TIN provided is correct and that I am a U.S. person. *
                    </label>
                </Section>

                {error && <div className="text-sm text-destructive bg-destructive/5 border border-destructive/15 rounded-lg px-4 py-2.5">{error}</div>}

                <button data-testid="apply-submit" onClick={submit} disabled={submitting}
                    className="w-full py-3 bg-phoenix-navy text-white font-semibold rounded-xl hover:bg-phoenix-navy-2 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                    {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Submit application
                </button>
            </div>
        </div>
    );
}
